import { Chip } from '@mui/material'
import {
  FlexRow,
  InputGroup,
  Label,
  PrimaryButton,
  TextAreaInput,
} from 'components/lib'
import StyledSlider from 'components/lib/Forms/Slider'
import { Formik } from 'formik'
import React, { useMemo, useState, useEffect } from 'react'
import {
  AssistantConfiguration,
  AssistantTone,
  Timezone,
} from '@super-software-inc/foundation'
import { updateAssistantConfiguration } from 'api/assistant/knowledgeBase'
import { toastError, toastSuccess } from 'components/lib/Toast'
import { useRecoilState, useRecoilValue } from 'recoil'
import { assistantConfigAtom, authenticatedUserAtom } from 'state/atoms'
import FixedBottomButtons from 'components/lib/Forms/FixedBottomButtons'
import TimezoneSelector from 'components/app/Dropdowns/TimezoneSelector'
import { updateCompany, findCompanyById } from 'api/companies'

import CustomizeVoiceAgentOptions from './CustomizeVoiceAgentOptions'
import { SectionLabel } from './CustomizeAgentShared'
import ScheduleField from './ScheduleField'
import {
  transformBackendScheduleToUI,
  transformScheduleForBackend,
  getEmptySchedule,
} from '../../../utils/schedule/transformers'

const toneChoices = [
  AssistantTone.Professional,
  AssistantTone.Formal,
  AssistantTone.Direct,
  AssistantTone.Polite,
  AssistantTone.Personable,
  AssistantTone.Casual,
  AssistantTone.Friendly,
]

const toSentenceCase = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1)

const CustomizeAgent = ({
  initialValues,
}: {
  initialValues: Partial<AssistantConfiguration>
}) => {
  const { selectedContact } = useRecoilValue(authenticatedUserAtom)
  const [authenticatedUser, setAuthenticatedUser] = useRecoilState(
    authenticatedUserAtom,
  )
  const [, setAssistantConfig] = useRecoilState(assistantConfigAtom)
  const [isScheduleValid, setIsScheduleValid] = useState(true)

  // Ensure voice-related fields have default values if not provided
  const formInitialValues = useMemo(
    () => ({
      ...initialValues,
    }),
    [initialValues],
  )

  // Fetch latest company data on mount
  useEffect(() => {
    async function fetchCompanyData() {
      if (!authenticatedUser.selectedCompany.id) return
      const latestCompany = await findCompanyById(
        authenticatedUser.selectedCompany.id,
      )
      if (latestCompany) {
        setAuthenticatedUser(prev => ({
          ...prev,
          selectedCompany: {
            id: prev.selectedCompany.id,
            ...latestCompany,
          },
        }))
        // console.log(
        //   '[CustomizeAgent] Business hours after fetch:',
        //   latestCompany.businessHours,
        // )
      }
    }
    // console.log(
    //   '[CustomizeAgent] Business hours on mount:',
    //   authenticatedUser.selectedCompany.businessHours,
    // )
    fetchCompanyData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setSelectedTimezone = (timezone: Timezone) => {
    updateCompany({
      ...authenticatedUser.selectedCompany,
      timezone,
    })
      .then(company => {
        toastSuccess(`Company timezone updated to ${timezone}`)
        setAuthenticatedUser({
          ...authenticatedUser,
          selectedCompany: {
            ...authenticatedUser.selectedCompany,
            timezone,
          },
        })
      })
      .catch(error => {
        toastError('Failed to update company timezone')
      })
  }

  return (
    <div>
      <Formik
        initialValues={{
          ...formInitialValues,
          businessHours:
            'businessHours' in authenticatedUser.selectedCompany &&
            authenticatedUser.selectedCompany.businessHours
              ? transformBackendScheduleToUI(
                  authenticatedUser.selectedCompany.businessHours,
                )
              : getEmptySchedule(),
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setSubmitting(true)
            // 1. Update company business hours
            const newBusinessHours = transformScheduleForBackend(
              values.businessHours,
            )
            await updateCompany({
              ...authenticatedUser.selectedCompany,
              businessHours: newBusinessHours,
            })
            // console.log('[CustomizeAgent] updateCompany payload:', {
            //   ...authenticatedUser.selectedCompany,
            //   businessHours: newBusinessHours,
            // })
            // Fetch latest company data after save
            const latestCompany = await findCompanyById(
              authenticatedUser.selectedCompany.id,
            )
            if (latestCompany) {
              setAuthenticatedUser({
                ...authenticatedUser,
                selectedCompany: {
                  id: authenticatedUser.selectedCompany.id,
                  ...latestCompany,
                },
              })
              // console.log(
              //   '[CustomizeAgent] Business hours after save:',
              //   latestCompany.businessHours,
              // )
            }
            // 2. Update assistant configuration (omit businessHours and timeRestrictions)
            const { businessHours, ...assistantConfig } = values
            if ('timeRestrictions' in assistantConfig) {
              delete (assistantConfig as any).timeRestrictions
            }
            // console.log('Assistant config being sent:', assistantConfig)
            const updatedConfig = await updateAssistantConfiguration(
              selectedContact.companyId,
              assistantConfig,
            )
            setAssistantConfig(updatedConfig)
            toastSuccess('Settings updated')
          } catch (error) {
            toastError('Failed to update settings')
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          isSubmitting,
          setValues,
          setFieldValue,
          errors,
          touched,
          resetForm,
        }) => (
          <form style={{ marginTop: 20 }} onSubmit={handleSubmit}>
            <SectionLabel>
              <span>General</span>
            </SectionLabel>
            <FlexRow align="center" style={{ marginBottom: 24 }}>
              <Label style={{ marginRight: 16, marginBottom: 0 }}>
                Company timezone:
              </Label>
              <TimezoneSelector
                selectedValue={authenticatedUser.selectedCompany.timezone}
                setSelectedValue={setSelectedTimezone}
              />
            </FlexRow>
            {/* Business hours Section */}
            <div className="space-y-1 mb-6">
              <Label className="block text-sm font-semibold text-gray-900">
                Business hours
              </Label>
              <ScheduleField
                value={values.businessHours}
                onChange={sched => setFieldValue('businessHours', sched)}
                errors={errors}
                touched={touched}
                onValidationChange={setIsScheduleValid}
              />
            </div>
            <InputGroup>
              <Label>Can not answer</Label>
              <TextAreaInput
                name="disallowedTopics"
                placeholder="Describe what your assistant should not answer"
                value={values.disallowedTopics}
                onChange={handleChange}
              />
            </InputGroup>
            <InputGroup>
              <Label>Can answer</Label>
              <TextAreaInput
                name="allowedTopics"
                placeholder="Describe what your assistant is allowed to answer"
                value={values.allowedTopics}
                onChange={handleChange}
              />
            </InputGroup>

            <InputGroup>
              <Label>Greeting used for calls</Label>
              <TextAreaInput
                name="greeting"
                placeholder="Greeting used for calls"
                value={values.greeting}
                onChange={handleChange}
              />
            </InputGroup>
            <InputGroup>
              <SectionLabel>Voice settings</SectionLabel>
              <FlexRow align="center">
                <Label>Creativity:</Label>
                <div className="flex w-[280px] ml-10">
                  <StyledSlider
                    value={values.temperature ? -values.temperature : undefined}
                    valueLabelDisplay="auto"
                    shiftStep={0.0625}
                    step={0.0625}
                    valueLabelFormat={
                      values.temperature === 0.9
                        ? 'Most creative'
                        : values.temperature === 0.8375
                        ? 'Creative'
                        : values.temperature === 0.775
                        ? 'Balanced'
                        : values.temperature === 0.7125
                        ? 'Predictable'
                        : 'Most predictable'
                    }
                    marks
                    min={-0.9}
                    max={-0.65}
                    onChange={(_, n) => {
                      handleChange({
                        target: { name: 'temperature', value: -n },
                      })
                    }}
                  />
                </div>
              </FlexRow>
            </InputGroup>
            <InputGroup>
              <Label>Tone</Label>

              {toneChoices.map(tone => (
                <Chip
                  key={tone}
                  label={toSentenceCase(tone)}
                  style={{
                    margin: 5,
                    backgroundColor: values.tone?.includes(tone)
                      ? '#0A1F44'
                      : 'white',
                    color: values.tone?.includes(tone) ? 'white' : '#627088',
                  }}
                  variant={values.tone?.includes(tone) ? 'filled' : 'outlined'}
                  onClick={() => {
                    const newTones = values.tone?.includes(tone)
                      ? values.tone?.filter(t => t !== tone)
                      : values.tone
                      ? [...values.tone, tone]
                      : [tone]
                    handleChange({
                      target: { name: 'tone', value: newTones },
                    })
                  }}
                />
              ))}
            </InputGroup>
            <InputGroup>
              <CustomizeVoiceAgentOptions
                values={values}
                setFieldValue={setFieldValue}
              />
            </InputGroup>
            <FixedBottomButtons>
              <FlexRow style={{ gap: 10 }} justify="flex-end">
                <PrimaryButton
                  type="button"
                  light
                  onClick={() => {
                    resetForm()
                  }}
                >
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={isSubmitting || !isScheduleValid}
                >
                  {isSubmitting ? 'Saving...' : 'Save customization'}
                </PrimaryButton>
              </FlexRow>
            </FixedBottomButtons>
          </form>
        )}
      </Formik>
    </div>
  )
}

export default CustomizeAgent
